/* eslint-disable react-hooks/exhaustive-deps */
// ════════════════════════════════════════════════════════════════
// SafetyScreeningScreen.jsx (Conversational)
// Red-flag symptom checklist for ALL body areas.
// LBP: 3-path model (STOP, FUNCTIONAL_GATE with follow-ups, CONTINUE)
// HKT: simple referral (any checked = STOP, none = CONTINUE)
// Converted: pushes Myra message on mount, returns inlineUi only,
// pushes frozen widgets + user echoes on completion.
// ════════════════════════════════════════════════════════════════

import React, { useState, useCallback, useEffect } from "react";
import { useScreenVoice } from "./useScreenVoice";
import { SAFETY_QUESTIONS } from "./GSA_CONSTANTS";
import { MYRA_MESSAGES } from "./GSA_MYRA_MESSAGES";
import styles from "./SelfAssessment.module.css";

let _ssId = 80000;
function nextSsId() {
  return ++_ssId;
}

export default function SafetyScreeningScreen({ assessmentData, pushToHistory, showMyraMessage, scrollToBottom, onContinue, onHardStop }) {
  const problemArea = assessmentData.problemArea;
  const safetyData = SAFETY_QUESTIONS[problemArea];

  const [checkedItems, setCheckedItems] = useState(new Set());
  const [locked, setLocked] = useState(false);
  const [ready, setReady] = useState(false);
  const [phase, setPhase] = useState("checklist"); // 'checklist' | 'followup'
  const [followUpQueue, setFollowUpQueue] = useState([]);
  const [safetyOutcome, setSafetyOutcome] = useState("CONTINUE");

  // ── Push divider + Myra message on mount ──
  useEffect(() => {
    if (!safetyData) return;
    let cancelled = false;
    async function init() {
      pushToHistory({ type: "divider", id: nextSsId() });
      await showMyraMessage(safetyData.prompt, 900);
      if (!cancelled) setReady(true);
    }
    init();
    return () => {
      cancelled = true;
    };
  }, []);

  // ── Voice ──
  useScreenVoice({
    screenName: "SafetyScreeningScreen",
    trackerName: "safety_screening",
    flowName: "flow_gsa",
    payloadKey: "SafetyScreeningScreen",
    commands: {
      ANSWER_MidJourneyFlow_QUESTION: (value) => {
        if (value === "continue_command") handleSubmit();
      },
      MOVE_ON: () => handleSubmit(),
    },
    exitReason: "safety_screening_completed",
  });

  // ── Toggle a safety item ──
  const toggleItem = useCallback(
    (id) => {
      if (locked) return;
      setCheckedItems((prev) => {
        const next = new Set(prev);
        if (next.has(id)) next.delete(id);
        else next.add(id);
        return next;
      });
    },
    [locked],
  );

  // ── Submit checklist ──
  const handleSubmit = useCallback(() => {
    if (locked) return;
    setLocked(true);

    const checked = [...checkedItems];

    // No items checked → clear, continue
    if (checked.length === 0) {
      // Push frozen widget + user echo
      pushToHistory({ type: "frozen-widget", widgetType: "safety_checklist", id: nextSsId(), data: { result: "none", labels: "" } }, { type: "user", text: "None of these apply", id: nextSsId() });

      // gtag conversion
      if (window.gtag) {
        window.gtag("event", "conversion", {
          send_to: "AW-18002481001/kXlOCNefkpccEOmeoIhD",
          value: 1.0,
          currency: "USD",
        });
      }
      onContinue({
        safetyScreenResult: "CLEAR",
        safetyScreenSelection: [],
        safetyOutcome: "CONTINUE",
      });
      return;
    }

    // Build labels for user echo
    const labels = checked.map((id) => safetyData.conditions.find((c) => c.id === id)?.label).filter(Boolean);

    // Push frozen widget + user echo
    pushToHistory({ type: "frozen-widget", widgetType: "safety_checklist", id: nextSsId(), data: { result: "checked", labels: labels.join(", ") } }, { type: "user", text: labels.join(", "), id: nextSsId() });

    // Check for immediate STOP conditions
    const hasStop = checked.some((id) => {
      const c = safetyData.conditions.find((x) => x.id === id);
      return c && c.path === "STOP";
    });

    if (hasStop) {
      onHardStop("safety");
      return;
    }

    // Build follow-up queue (LBP 3-path: FUNCTIONAL_GATE items have follow-ups)
    const queue = checked.map((id) => safetyData.conditions.find((c) => c.id === id)).filter((c) => c && c.followUp);

    if (queue.length > 0) {
      setFollowUpQueue(queue);
      setPhase("followup");
      setLocked(false);
      // Push Myra transition to follow-up
      showMyraMessage(MYRA_MESSAGES.safety?.followUpIntro || "Thanks for letting me know. I have a couple of follow-up questions to make sure it's safe to continue.", 800);
    } else {
      // HKT simple model: any checked = STOP (all HKT conditions have path: "STOP")
      // If we got here, all checked items were non-STOP, non-followUp → continue
      if (window.gtag) {
        window.gtag("event", "conversion", {
          send_to: "AW-18002481001/kXlOCNefkpccEOmeoIhD",
          value: 1.0,
          currency: "USD",
        });
      }
      onContinue({
        safetyScreenResult: "CLEAR",
        safetyScreenSelection: checked,
        safetyOutcome: "CONTINUE",
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [checkedItems, locked, safetyData]);

  // ── Handle follow-up answer ──
  const handleFollowUpAnswer = useCallback(
    (conditionId, answerId) => {
      const condition = safetyData.conditions.find((c) => c.id === conditionId);
      const answer = condition.followUp.options.find((o) => o.id === answerId);

      // Push user echo for the follow-up answer
      pushToHistory({ type: "user", text: answer.label, id: nextSsId() });

      if (answer.outcome === "STOP") {
        onHardStop("safety");
        return;
      }

      if (answer.outcome === "SEATED_ONLY") {
        setSafetyOutcome("SEATED_ONLY");
      }

      // Advance to next follow-up or finish
      const remaining = followUpQueue.slice(1);
      if (remaining.length > 0) {
        setFollowUpQueue(remaining);
        showMyraMessage(MYRA_MESSAGES.safety?.followUpNext || "One more thing I need to check.", 600);
      } else {
        const finalOutcome = answer.outcome === "SEATED_ONLY" || safetyOutcome === "SEATED_ONLY" ? "SEATED_ONLY" : "CONTINUE";
        // gtag conversion on clear
        if (window.gtag) {
          window.gtag("event", "conversion", {
            send_to: "AW-18002481001/kXlOCNefkpccEOmeoIhD",
            value: 1.0,
            currency: "USD",
          });
        }
        onContinue({
          safetyScreenResult: "CLEAR",
          safetyScreenSelection: [...checkedItems],
          safetyOutcome: finalOutcome,
        });
      }
      // eslint-disable-next-line react-hooks/exhaustive-deps
    },
    [followUpQueue, safetyOutcome, safetyData, checkedItems],
  );

  // ── No safety data fallback ──
  useEffect(() => {
    if (!safetyData) {
      onContinue({ safetyScreenResult: "CLEAR", safetyScreenSelection: [], safetyOutcome: "CONTINUE" });
    }
  }, [safetyData, onContinue]);

  if (!safetyData) return null;
  if (!ready) return null;

  // ── Follow-up phase ──
  if (phase === "followup" && followUpQueue.length > 0) {
    const current = followUpQueue[0];
    return (
      <div className={styles.inlineUi}>
        <div className={styles.followupCard}>
          <div className={styles.followupQuestions}>
            <p className={styles.followupQ}>
              <span className={styles.followupNum}>1.</span> {current.followUp.q1}
            </p>
            <p className={styles.followupQ}>
              <span className={styles.followupNum}>2.</span> {current.followUp.q2}
            </p>
          </div>

          <div className={styles.optionCards}>
            {current.followUp.options.map((opt) => (
              <button key={opt.id} className={styles.optionCard} onClick={() => handleFollowUpAnswer(current.id, opt.id)}>
                {opt.label}
              </button>
            ))}
          </div>
        </div>
      </div>
    );
  }

  // ── Checklist phase ──
  const checkSvg = (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round">
      <polyline points="20 6 9 17 4 12" />
    </svg>
  );

  return (
    <div className={styles.inlineUi}>
      <div className={styles.safetyChecklist}>
        {safetyData.conditions.map((c) => {
          const isChecked = checkedItems.has(c.id);
          return (
            <div key={c.id} className={`${styles.safetyItem} ${isChecked ? styles.checked : ""} ${locked ? styles.locked : ""}`} onClick={() => toggleItem(c.id)}>
              <div className={styles.safetyCheckbox}>
                <span className={styles.safetyCheckboxCheck}>{checkSvg}</span>
              </div>
              <span className={styles.safetyLabel}>{c.label}</span>
            </div>
          );
        })}
      </div>

      <p className={styles.safetyHelper}>If none of these apply to you, just tap Continue</p>

      <button className={styles.safetyContinue} onClick={handleSubmit} disabled={locked}>
        Continue
      </button>
    </div>
  );
}
